// components/director — the pieces the home Dashboard, Portfolio roll-ups and home blocks share (Task M).
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Cell } from 'recharts';
import { ArrowRight, Download } from 'lucide-react';
import { StatTile } from '../charts/StatTile';
import { Drawer } from '../building/bits';
import { formatCell, seriesColor, STATUS, TEXT_MUTED } from '../../lib/viz';
import { directorApi, downloadReportCsv, fmtMoney, monthLabel, type Drill, type Figure } from '../../lib/director';
import type { ReportResult } from '../../types/api';
import { SkeletonRows } from '../ui';

/** A director figure as a stat tile; clicking opens the rows behind it. A figure the viewer may not read is not shown. */
export function FigureTile({ label, figure, kind = 'number', hint, status, onDrill }: {
  label: string; figure: Figure | null; kind?: 'number' | 'money' | 'percent'; hint?: string; status?: keyof typeof STATUS; onDrill: (d: Drill, title: string) => void;
}) {
  if (!figure) return null;
  const v = kind === 'money' ? fmtMoney(figure.value, figure.currency, true) : kind === 'percent' ? `${figure.value}%` : String(figure.value);
  return <StatTile compact label={label} value={v} hint={hint ?? figure.note} status={status} onClick={() => onDrill(figure.drill, label)} />;
}

/** Twelve (or six) little bars: a trend in the corner of a panel. */
export function Sparkbars({ data, series, currency }: { data: Array<Record<string, number | string>>; series: Array<{ key: string; label: string; colour?: string }>; currency: string }) {
  if (!data.length) return null;
  return (
    <div className="h-28 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }} barGap={2}>
          <XAxis dataKey="month" tickFormatter={(m) => monthLabel(String(m))} tick={{ fontSize: 10, fill: TEXT_MUTED }} axisLine={false} tickLine={false} />
          <Tooltip formatter={(v, name) => [fmtMoney(Number(v ?? 0), currency), series.find((s) => s.key === String(name))?.label ?? String(name)]} labelFormatter={(m) => monthLabel(String(m))} cursor={{ fill: 'var(--color-ink-100)' }} />
          {series.map((s, i) => <Bar key={s.key} dataKey={s.key} radius={[3, 3, 0, 0]} fill={s.colour ?? seriesColor(i)}>{data.map((_, j) => <Cell key={j} fillOpacity={j === data.length - 1 ? 0.55 : 1} />)}</Bar>)}
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-ink-500">{series.map((s, i) => <span key={s.key} className="inline-flex items-center gap-1"><i className="inline-block h-2 w-2 rounded-sm" style={{ background: s.colour ?? seriesColor(i) }} />{s.label}</span>)}</div>
    </div>
  );
}

/** A small table of a report's rows (drill-through and roll-ups). */
export function ReportTable({ result, onRow, hidden = [] }: { result: ReportResult; onRow?: (row: Record<string, unknown>) => void; hidden?: string[] }) {
  const cols = result.columns.filter((c) => !hidden.includes(c.key));
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const rows = sort ? [...result.rows].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key];
    if (x === null || x === undefined) return 1; if (y === null || y === undefined) return -1;
    return (typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'en-GB', { numeric: true })) * sort.dir;
  }) : result.rows;
  if (!result.rows.length) return <p className="py-10 text-center text-[13px] text-ink-400">Nothing behind this number.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max text-[12.5px]">
        <thead><tr className="bg-ink-50/60 text-left">
          {cols.map((c, i) => (
            <th key={c.key} className={`px-3 py-1.5 text-[11.5px] font-medium uppercase tracking-wide text-ink-500 ${c.type !== 'string' && c.type !== 'date' ? 'text-right' : ''} ${i === 0 ? 'sticky left-0 bg-ink-50' : ''}`}>
              <button type="button" className="uppercase tracking-wide hover:text-ink-900" onClick={() => setSort((s) => (!s || s.key !== c.key ? { key: c.key, dir: 1 } : s.dir === 1 ? { key: c.key, dir: -1 } : null))}>
                {c.label}{sort?.key === c.key ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
              </button>
            </th>
          ))}
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} onClick={onRow ? () => onRow(r) : undefined} className={`border-t border-ink-100 ${onRow ? 'cursor-pointer hover:bg-ink-50/60' : ''}`}>
              {cols.map((c, j) => (
                <td key={c.key} className={`px-3 py-1.5 text-[12.5px] text-ink-800 ${c.type !== 'string' && c.type !== 'date' ? 'text-right tabular-nums' : ''} ${j === 0 ? 'sticky left-0 bg-white font-medium text-ink-900' : ''}`}>{formatCell(r[c.key], c.type, result.currency)}</td>
              ))}
            </tr>
          ))}
        </tbody>
        {result.totals && Object.keys(result.totals).length > 0 && result.rows.length > 1 && (
          <tfoot><tr className="border-t border-ink-200 bg-ink-50/60 font-semibold">
            {cols.map((c, j) => <td key={c.key} className={`px-3 py-2 ${c.type !== 'string' && c.type !== 'date' ? 'text-right tabular-nums' : ''} ${j === 0 ? 'sticky left-0 bg-ink-50' : ''}`}>{j === 0 ? 'Everything' : (c.type === 'money' || c.type === 'number') && result.totals![c.key] !== undefined ? formatCell(result.totals![c.key], c.type, result.currency) : ''}</td>)}
          </tr></tfoot>
        )}
      </table>
    </div>
  );
}

/** The rows behind a number: run the drill's report in a drawer, with a link to the page that owns them. */
export function DrillDrawer({ drill, title, onClose }: { drill: Drill; title: string; onClose: () => void }) {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['drill', drill.reportKey, drill.filters], queryFn: () => directorApi.runReport(drill.reportKey!, drill.filters ?? {}), enabled: !!drill.reportKey });
  const filters = Object.fromEntries(Object.entries(drill.filters ?? {}).map(([k, v]) => [k, String(v)]));
  return (
    <Drawer title={title} subtitle={q.data ? `${q.data.title} · ${q.data.rows.length} row${q.data.rows.length === 1 ? '' : 's'}` : 'The rows behind the number'} onClose={onClose}
      footer={<>
        {drill.reportKey && <button type="button" onClick={() => void downloadReportCsv(drill.reportKey!, filters)} className="inline-flex items-center gap-1 text-[12.5px] font-medium text-bronze-700 hover:underline"><Download className="h-3.5 w-3.5" /> CSV</button>}
        {drill.path && <Link to={drill.path} className="inline-flex h-8 items-center gap-1 rounded-full bg-bronze-500 px-3.5 text-[12.5px] font-medium text-on-primary transition hover:bg-bronze-600">Open the page <ArrowRight className="h-3.5 w-3.5" /></Link>}
      </>}>
      {!drill.reportKey ? <p className="py-6 text-[13px] text-ink-500">This number is kept on its own page. <button type="button" className="font-medium text-bronze-700 hover:underline" onClick={() => drill.path && navigate(drill.path)}>Open it →</button></p>
        : q.isLoading ? <SkeletonRows rows={6} cols={4} className="py-4" />
        : q.error ? <p className="py-6 text-[13px] text-red-600">{(q.error as Error).message}</p>
        : q.data ? <ReportTable result={q.data} /> : null}
    </Drawer>
  );
}

/** One question's panel: heading, a row of figure tiles, optionally a trend. */
export function Panel({ title, subtitle, children, trend }: { title: string; subtitle?: string; children: React.ReactNode; trend?: React.ReactNode }) {
  return (
    <section className="surface p-4">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h2 className="text-[13px] font-semibold text-ink-900">{title}</h2>
        {subtitle && <span className="text-[11.5px] text-ink-400">{subtitle}</span>}
      </div>
      <div className="tile-row">{children}</div>
      {trend && <div className="mt-4">{trend}</div>}
    </section>
  );
}
